/**
 * "Gesprek met … gehad?" (spec-68): tapping a phone number remembers, in `sessionStorage`, which
 * relation was called and when. Back on that relation's page within two hours the banner asks.
 * Every storage access is guarded: a blocked or full storage means no banner, never a crash.
 */
export const CALL_KEY = "vonk.crm.gebeld";
export const CALL_WINDOW_MS = 2 * 60 * 60 * 1000;

export interface CallRelation {
  type: "contact" | "organisatie";
  id: string;
  name: string;
}

interface CallEntry extends CallRelation {
  at: number;
}

export function recordCall(
  relation: CallRelation,
  now: number = Date.now(),
): void {
  try {
    const entry: CallEntry = { ...relation, at: now };
    window.sessionStorage.setItem(CALL_KEY, JSON.stringify(entry));
  } catch {
    // No storage, no banner.
  }
}

export function clearCall(): void {
  try {
    window.sessionStorage.removeItem(CALL_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** The call to ask about on this relation's page, or null. An expired entry is removed. */
export function pendingCall(
  type: CallRelation["type"],
  id: string,
  now: number = Date.now(),
): CallEntry | null {
  try {
    const raw = window.sessionStorage.getItem(CALL_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as Partial<CallEntry> | null;
    if (
      !entry ||
      typeof entry.at !== "number" ||
      typeof entry.id !== "string"
    ) {
      clearCall();
      return null;
    }
    if (now - entry.at > CALL_WINDOW_MS) {
      clearCall();
      return null;
    }
    if (entry.type !== type || entry.id !== id) return null;
    return entry as CallEntry;
  } catch {
    return null;
  }
}

/** The dialable form of a phone number: digits and a leading plus. */
export function telHref(phone: string): string {
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, "");
  return `tel:${trimmed.startsWith("+") ? "+" : ""}${digits}`;
}
